import React from 'react'

import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { type Theme, useTheme } from '@mui/material/styles'
import { BarPlot, ChartContainer, ChartsReferenceLine, ChartsXAxis } from '@mui/x-charts'
import { useContainerDimensions } from '../../../../hooks/useContainerDimensions'

import { DataSet, DoubleBarChartProps } from '../../../../types/Charts/BarChart'

import { CustomDoubleBarPlot } from './CustomDoubleBarPlot'
import { DoubleBarChartLegend } from './DoubleBarChartLegend'

export const DoubleBarChart = ({
  amounts,
  barGapRatio = 0,
  categoryGapRatio = 0.2,
  colors,
  data,
  isMini = false,
  legendLabelComparison,
  legendLabelMain,
  margin = {
    bottom: 30,
    left: 40,
    right: 40,
    top: 20,
  },
  onBarClick,
}: DoubleBarChartProps) => {
  const theme = useTheme()
  const containerRef = React.useRef<HTMLDivElement>(null)
  const { height, width } = useContainerDimensions(containerRef)

  const handleBarClick = ({ dataIndex, seriesId }: { dataIndex: number; seriesId: string }) => {
    const seriesType = seriesId.toString().split('-').pop()
    onBarClick?.(dataIndex * 2 + Number(seriesType))
  }

  return (
    <Stack alignItems="center" sx={{ mb: isMini ? 16 : 24, width: '100%' }}>
      <Box
        ref={containerRef}
        sx={{
          height: isMini ? 120 : 172,
          mb: 8,
          touchAction: 'pan-y',
          width: '100%',
          '& svg': {
            touchAction: 'pan-y',
            userSelect: 'none',
          },
          '& .recharts-wrapper': {
            touchAction: 'pan-y',
          },
        }}
      >
        <ChartContainer
          colors={colors}
          dataset={data as any[]}
          height={height}
          margin={margin}
          series={[
            { dataKey: 'main', type: 'bar' },
            { dataKey: 'comparison', type: 'bar' },
          ]}
          width={width}
          xAxis={[{ barGapRatio, categoryGapRatio, dataKey: 'label', scaleType: 'band' }]}
          yAxis={[{ width: 0 }]}
        >
          {amounts?.average && (
            <ChartsReferenceLine
              labelAlign="middle"
              lineStyle={{
                stroke: (theme as Theme).palette.neutral?.dark,
                strokeDasharray: '0.1 5.9',
                strokeLinecap: 'round',
                strokeWidth: 2,
              }}
              y={amounts?.average}
            />
          )}
          <ChartsXAxis
            disableTicks={true}
            sx={(theme) => ({
              '.MuiChartsAxis-line': {
                stroke: (theme as Theme).palette.neutral?.dark,
              },
            })}
            tickLabelStyle={{
              fontSize: 11,
              fill: theme.palette.text.secondary,
              fontWeight: 400,
            }}
          />
          <BarPlot
            onItemClick={(_, { dataIndex, seriesId }) =>
              handleBarClick({ dataIndex, seriesId: seriesId.toString() })
            }
            slots={{
              bar: (props) => {
                const { ownerState, ...defaultProps } = props
                return (
                  <CustomDoubleBarPlot
                    {...defaultProps}
                    color={ownerState.color}
                    data={data as DataSet[]}
                    series={{
                      index: ownerState.dataIndex,
                      type: Number(ownerState.id.toString().split('-').pop()),
                    }}
                    style={{
                      height: props.height,
                      x: props.x,
                      y: props.y,
                      width: props.width,
                    }}
                  />
                )
              },
            }}
          />
        </ChartContainer>
      </Box>

      {(legendLabelComparison || legendLabelMain) && (
        <DoubleBarChartLegend
          comparisonColor={colors[1]}
          legendLabelComparison={legendLabelComparison}
          legendLabelMain={legendLabelMain}
          legendStyle={{ mr: margin.left! + margin.right! }}
          mainColor={colors[0]}
        />
      )}
    </Stack>
  )
}
